import React, { useState, useEffect, useRef } from 'react';

interface Option {
  id: string | number;
  nome?: string;
  descricao?: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string | number | '';
  onChange: (value: string | number | '') => void;
  placeholder?: string;
  label?: string;
  error?: string;
  onSearch?: (searchTerm: string) => void;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  size?: 'normal' | 'lg' | 'xl';
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione uma opção",
  label,
  error,
  onSearch,
  loading = false,
  disabled = false,
  required = false,
  size = 'normal'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const paddingCls = size === 'xl' ? 'py-4' : size === 'lg' ? 'py-3' : 'py-2';
  const textCls = size === 'xl' ? 'text-2xl' : size === 'lg' ? 'text-xl' : 'text-base';

  // Helpers para normalizar comparação de IDs
  const toIdString = (v: string | number | '') => (v !== '' && v !== undefined && v !== null) ? String(v) : '';

  // Encontrar a opção selecionada (comparando como string)
  const selectedOption = options.find(option => toIdString(option.id) === toIdString(value));
  const displayText = selectedOption ? (selectedOption.nome || selectedOption.descricao || '') : '';

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // Filtrar opções localmente sempre que termo ou opções mudarem
  useEffect(() => {
    const filtered = options.filter(option => {
      const searchLower = (searchTerm || '').toLowerCase();
      const searchDigits = (searchTerm || '').replace(/[^\d]/g, '');
      const nome = option.nome?.toLowerCase() || '';
      const descricao = option.descricao?.toLowerCase() || '';
      const nomeDigits = (option.nome || '').replace(/[^\d]/g, '');
      const descricaoDigits = (option.descricao || '').replace(/[^\d]/g, '');
      const textMatch = nome.includes(searchLower) || descricao.includes(searchLower);
      const digitsMatch = !!searchDigits && (nomeDigits.includes(searchDigits) || descricaoDigits.includes(searchDigits));
      return textMatch || digitsMatch;
    });
    setFilteredOptions(filtered);
    setHighlightedIndex(0);
  }, [searchTerm, options]);

  // Disparar pesquisa externa com debounce apenas quando o termo mudar
  useEffect(() => {
    if (!onSearch) return;
    const term = searchTerm.trim();
    if (term.length < 2) return;
    const handler = setTimeout(() => onSearch(term), 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Garantir que o item destacado esteja visível
    const list = listRef.current;
    if (list && isOpen) {
      const child = list.children[highlightedIndex] as HTMLElement | undefined;
      if (child) child.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const handleOptionSelect = (option: Option) => {
    onChange(toIdString(option.id));
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) handleOptionSelect(option);
    } else if (e.key === 'Tab') {
      const option = filteredOptions[highlightedIndex];
      if (option) {
        handleOptionSelect(option);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleClosedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key.length === 1 || e.key === 'Enter' || e.key === ' ') {
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div
          className={`
            w-full px-3 ${paddingCls} border rounded-md cursor-pointer
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : ''}
          `}
          onClick={handleInputClick}
          onFocus={() => {
            if (!disabled) {
              setIsOpen(true);
              setTimeout(() => inputRef.current?.focus(), 50);
            }
          }}
          onKeyDown={handleClosedKeyDown}
          tabIndex={disabled ? -1 : 0}
        >
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Digite para pesquisar..."
              className={`w-full outline-none bg-transparent ${textCls}`}
              disabled={disabled}
            />
          ) : (
            <div className="flex items-center justify-between">
              <span className={`${displayText ? 'text-gray-900' : 'text-gray-500'} ${textCls}`}>
                {displayText || placeholder}
              </span>
              <div className="flex items-center space-x-1">
                {value && !disabled && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {isOpen && (
          <div ref={listRef} className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto min-w-[240px] w-full">
            {loading ? (
              <div className="px-3 py-2 text-gray-500 text-center">
                Carregando...
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => (
                <div
                  key={option.id}
                  className={`
                    px-3 py-2 cursor-pointer hover:bg-gray-100
                    ${idx === highlightedIndex ? 'bg-blue-50 text-blue-700' : ''}
                    ${toIdString(option.id) === toIdString(value) ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}
                  `}
                  onMouseDown={(e) => {
                    // Seleciona antes do blur do input
                    e.preventDefault();
                    handleOptionSelect(option);
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  <div className="font-medium">
                    {option.nome || option.descricao}
                  </div>
                  {option.nome && option.descricao && option.nome !== option.descricao && (
                    <div className="text-sm text-gray-500">
                      {option.descricao}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-center text-sm">
                Nenhuma opção encontrada
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};